import { useState, useEffect } from "react";
import { Clock } from "lucide-react";

const YOUTUBE_API_KEY = import.meta.env.VITE_YOUTUBE_API_KEY;

interface YouTubeTableCellProps {
  url: string;
}

const YouTubeTableCell = ({ url }: YouTubeTableCellProps) => {
  const [videoDetails, setVideoDetails] = useState<{
    title: string;
    showName: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // YouTube URL validation regex
  const validateYouTubeUrl = (url: string) => {
    const patterns = [
      /^https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
      /^https?:\/\/(?:www\.)?youtube\.com\/embed\/([^?]+)/,
      /^https?:\/\/(?:www\.)?youtu\.be\/([^?]+)/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        return match[1];
      }
    }
    return null;
  };

  const fetchVideoDetails = async (id: string) => {
    try {
      setIsLoading(true);
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/videos?part=snippet&id=${id}&key=${YOUTUBE_API_KEY}`,
      );
      const data = await response.json();

      if (data.items && data.items.length > 0) {
        const title = data.items[0].snippet.title as string;
        const parts = title.split("|").map((part) => part.trim());

        setVideoDetails({
          title: parts[0],
          showName: parts[1] || data.items[0].snippet.channelTitle,
        });
      }
    } catch (err) {
      console.error("Error fetching video details:", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const videoId = validateYouTubeUrl(url);
    if (videoId) {
      fetchVideoDetails(videoId);
    }
  }, [url]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 animate-spin" />
        <span className="text-muted-foreground">Loading...</span>
      </div>
    );
  }

  if (!videoDetails) {
    return <span>Invalid YouTube URL</span>;
  }

  return (
    <div className="flex items-center gap-2">
      <div className="flex flex-col">
        <span className="font-medium">{videoDetails.title}</span>
        <span className="text-sm text-muted-foreground">
          {videoDetails.showName}
        </span>
      </div>
    </div>
  );
};

export default YouTubeTableCell;
